import React from 'react';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrgGroups } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { UserGroupsForDirectoySyncGroup } from '../../types';
import { globalMessage } from '../../utils/globalMessage';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { displayTextForUserGroupSingular } from '../../helpers/displayHelpers';
import { tryUpdateOrgSettings } from '../../actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface EditUserGroupsForDirectorySyncGroupModalProps {
    open: boolean;
    orgId: string;
    onCancel: () => void;
    row: UserGroupsForDirectoySyncGroup;
}

export const EditGroupsForDirectorySyncGroupModal: React.FC<EditUserGroupsForDirectorySyncGroupModalProps> = (
    props: EditUserGroupsForDirectorySyncGroupModalProps,
) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const updateUserGroupsIsLoading = useIsLoading(OrgSettingsUpdatable.UpdateUserGroupsForDirectorySyncGroup);
    const updateUserGroupsError = useError(OrgSettingsUpdatable.UpdateUserGroupsForDirectorySyncGroup);
    const updateUserGroupsSuccess = useUpdated(OrgSettingsUpdatable.UpdateUserGroupsForDirectorySyncGroup);

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const updateUserGroups = (values) => {
        if (!props.row?.group) {
            return;
        }

        dispatch(
            tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateUserGroupsForDirectorySyncGroup, {
                orgId: props.orgId,
                groupId: props.row?.group.id,
                userGroups: values.userGroups,
            }),
        );
    };

    React.useEffect(() => {
        if (updateUserGroupsError) {
            globalMessage.error(updateUserGroupsError);
        }
    }, [updateUserGroupsError]);

    React.useEffect(() => {
        if (updateUserGroupsSuccess) {
            globalMessage.success('Runway user groups update queued for Directory Sync group.');

            props.onCancel();
        }
    }, [updateUserGroupsSuccess]);

    const selectOptions = Object.keys(orgGroupsById)
        .filter((id) => !orgGroupsById[id]?.disabled)
        .map((id) => ({ id, label: displayTextForUserGroupSingular(orgGroupsById[id]) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    return (
        <Modal
            title={`Edit Runway groups – ${props.row?.group.name}`}
            okButtonProps={{ loading: updateUserGroupsIsLoading }}
            onOk={() => form.submit()}
            okText="Apply"
            open={props.open}
            onCancel={props.onCancel}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form
                    onFinish={updateUserGroups}
                    initialValues={{
                        userGroups: props.row?.userGroups.map((group) => group.id) ?? [],
                    }}
                    form={form}
                    preserve={false}
                >
                    <Form.Item name="userGroups">
                        <Select mode="multiple" optionFilterProp="children" placeholder="Select Runway user groups">
                            {selectOptions.map(({ id, label }) => (
                                <Select.Option key={id} value={id}>
                                    {label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};
